import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { parseAsInteger, parseAsString, useQueryState } from "nuqs"
import { useMemo, useState } from "react"
import { toast } from "sonner"

import { PersonForm } from "@/components/people/person-form"
import { getPersonTableColumns } from "@/components/people/person-table"
import { PeoplePageSkeleton } from "@/components/skeletons/people-skeleton"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table/data-table"
import { DataTableToolbar } from "@/components/ui/data-table/data-table-toolbar"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useDataTable } from "@/hooks/use-data-table"
import { listPeople, type TPersonFilterType } from "@/http/people"
import { getSortingStateParser } from "@/lib/table/parsers"
import { useOrganization } from "@/store/organization"

/**
 * People index route - displays list of all people
 */
export const Route = createFileRoute("/_authenticated/$orgSlug/people/")({
  component: PeopleIndex,
  pendingComponent: PeoplePageSkeleton,
})

function PeopleIndex() {
  const navigate = useNavigate()
  const { currentOrg } = useOrganization()
  const [personType, setPersonType] = useState<TPersonFilterType>("customer")
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const [page] = useQueryState("page", parseAsInteger.withDefault(1))
  const [perPage] = useQueryState("perPage", parseAsInteger.withDefault(10))
  const [sorting] = useQueryState(
    "sort",
    getSortingStateParser().withDefault([]),
  )
  const [searchTerm] = useQueryState("firstName", parseAsString)

  const activeSort = sorting?.[0]
  const sortField =
    activeSort?.id === "email" || activeSort?.id === "firstName"
      ? activeSort.id
      : undefined
  const sortDesc = sortField ? (activeSort?.desc ?? false) : undefined
  const query = searchTerm?.trim() || undefined
  const filterFields = useMemo(
    () => [
      {
        id: "firstName",
        label: "Pesquisar",
        placeholder: "Buscar por nome, email ou telefone",
      },
    ],
    [],
  )

  // Fetch people data
  const { data, isLoading, refetch } = useSuspenseQuery({
    queryKey: [
      "people",
      currentOrg?.slug,
      personType,
      page,
      perPage,
      query,
      sortField,
      sortDesc,
    ],
    queryFn: async () => {
      const response = await listPeople({
        personType,
        organizationSlug: currentOrg?.slug ?? "",
        page,
        perPage,
        query,
        sort: sortField,
        desc: sortDesc,
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      return response.data
    },
  })

  const columns = useMemo(
    () =>
      getPersonTableColumns({
        organizationRole: currentOrg?.role ?? "member",
        organizationSlug: currentOrg?.slug ?? "",
        onEdit: (personId) => {
          navigate({
            to: "/$orgSlug/people/$personId",
            params: { orgSlug: currentOrg?.slug ?? "", personId },
            search: { action: "edit" },
          })
        },
        onDelete: (personId) => {
          navigate({
            to: "/$orgSlug/people/$personId",
            params: { orgSlug: currentOrg?.slug ?? "", personId },
            search: { action: "delete" },
          })
        },
      }),
    [currentOrg, navigate],
  )

  const pageCount = Math.max(1, Math.ceil((data?.total || 0) / perPage))

  const { table } = useDataTable({
    data: data?.data || [],
    columns,
    pageCount,
    filterFields,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  })

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false)
    refetch()
    toast.success("Pessoa criada com sucesso!")
  }

  if (!currentOrg) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Pessoas</h1>
          <p className="text-muted-foreground">
            Gerencie clientes e fornecedores
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Pessoa
        </Button>
      </div>

      {/* Type Filter */}
      <div className="flex gap-2">
        <Button
          variant={personType === "customer" ? "default" : "outline"}
          onClick={() => {
            setPersonType("customer")
            table.setPageIndex(0)
          }}
        >
          Clientes
        </Button>
        <Button
          variant={personType === "supplier" ? "default" : "outline"}
          onClick={() => {
            setPersonType("supplier")
            table.setPageIndex(0)
          }}
        >
          Fornecedores
        </Button>
      </div>

      {/* Data Table */}
      <div className="space-y-4">
        <DataTableToolbar table={table} filterFields={filterFields} />
        <DataTable table={table} total={data?.total} loading={isLoading} />
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Pessoa</DialogTitle>
          </DialogHeader>
          <PersonForm
            organizationSlug={currentOrg.slug}
            onSuccess={handleCreateSuccess}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
